import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, type LucideIcon } from 'lucide-react';

interface PhaseIntroCardProps {
  phase: number;
  title: string;
  description: string;
  icon: LucideIcon;
}

const collapsedKey = (phase: number) => `phase-intro-collapsed-${phase}`;

export default function PhaseIntroCard({ phase, title, description, icon: Icon }: PhaseIntroCardProps) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(collapsedKey(phase)) !== 'true'; }
    catch { return true; }
  });

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem(collapsedKey(phase), String(!next)); }
    catch { /* localStorage unavailable in private browsing */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
      className="mb-8 bg-card border border-border rounded-2xl overflow-hidden"
    >
      <div className="flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-5">

        {/* Phase icon */}
        <div className="shrink-0 mt-0.5 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-muted border border-border flex items-center justify-center">
          <Icon className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-gold" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Badge */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="h-[6px] w-[6px] rounded-full bg-gold shrink-0" />
            <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Current Phase
            </span>
          </div>

          {/* Title */}
          <h2 className="font-serif text-xl sm:text-2xl font-bold text-foreground leading-tight">
            Phase {phase}: {title}
          </h2>

          {/* Description — collapsible */}
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="desc"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                className="overflow-hidden"
              >
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="flex items-center shrink-0 -mr-1 -mt-0.5">
          <button
            onClick={handleToggle}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ChevronUp
              className={`w-4 h-4 transition-transform duration-200 ${expanded ? '' : 'rotate-180'}`}
            />
          </button>
        </div>

      </div>
    </motion.div>
  );
}
